import { ReactNode, Ref } from "react";
import { Box, Button } from "@mui/material";
import { maskedContent } from "helpers/maskedContent";
import styles from "../ContractCompare.styles";
import { compareColors as c } from "../ContractCompare.tokens";

export type PanelTone = "success" | "warning" | "neutral";

const TONES: Record<PanelTone, { fill: string; color: string; title: string }> = {
  success: { fill: c.successFill, color: c.success, title: c.success },
  warning: { fill: c.warningFill, color: c.warning, title: c.ink },
  neutral: { fill: c.chipFill, color: c.ink, title: c.ink },
};

export interface PanelAction {
  label: string;
  onClick: () => void;
  icon?: ReactNode;
  /** The filled brand button; every other action is the outlined one. */
  primary?: boolean;
}

interface OutcomePanelProps {
  tone: PanelTone;
  icon: ReactNode;
  title: string;
  text?: string;
  /** The supporting line is bold in the "no changes" frame. */
  textBold?: boolean;
  actions: PanelAction[];
  /** The heading takes focus when the panel appears: a screen reader hears what happened. */
  headingRef?: Ref<HTMLHeadingElement>;
  /** A failure (not a result) is announced at once. */
  alert?: boolean;
}

/**
 * The centred panel every no-list frame uses: an icon tile, a heading, one supporting line and the way forward.
 * Server words and file names inside it are customer-adjacent text and are masked from session recording (D41).
 */
const OutcomePanel = ({ tone, icon, title, text, textBold = false, actions, headingRef, alert = false }: OutcomePanelProps) => {
  const look = TONES[tone];
  return (
    <Box sx={styles.panelWrap}>
      <Box
        component="section"
        sx={styles.panel}
        role={alert ? "alert" : undefined}
        aria-labelledby="compare-outcome-title"
        {...maskedContent}
      >
        <Box sx={styles.panelIcon(look.fill, look.color)} aria-hidden="true">
          {icon}
        </Box>
        <Box component="h2" id="compare-outcome-title" ref={headingRef} tabIndex={-1} sx={styles.panelTitle(look.title)}>
          {title}
        </Box>
        {text && (
          <Box component="p" sx={styles.panelText(textBold)}>
            {text}
          </Box>
        )}
        {actions.length > 0 && (
          <Box sx={styles.panelActions}>
            {actions.map((action) => (
              <Button
                key={action.label}
                variant={action.primary ? "contained" : "outlined"}
                onClick={action.onClick}
                startIcon={action.icon}
                sx={styles.actionButton}
              >
                {action.label}
              </Button>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default OutcomePanel;
